import axios from "axios";

// In dev, .env points REACT_APP_BACKEND_URL at localhost -- fine when the
// page itself is opened via localhost, but "localhost" baked into the JS
// bundle resolves to the *viewing device* when the page is reached over
// LAN/VPN via this machine's IP, not to this machine. Swap in whatever
// host the page was actually loaded from (same port) in that case only;
// a real domain (production) passes through untouched.
export function resolveBackendBase() {
  const configured = process.env.REACT_APP_BACKEND_URL;
  const localMatch = configured && configured.match(/^https?:\/\/(?:localhost|127\.0\.0\.1)(:(\d+))?\/?$/i);
  if (localMatch && typeof window !== "undefined" && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) {
    const port = localMatch[2];
    return `${window.location.protocol}//${window.location.hostname}${port ? `:${port}` : ""}`;
  }
  return configured;
}

const BASE = `${resolveBackendBase()}/api`;
const TOKEN_KEY = "agri_token";

// Requests that hang (e.g. a brief backend restart) fail after this instead
// of spinning forever with no feedback.
const REQUEST_TIMEOUT_MS = 15000;
// Retries for hiccups only: no response at all (network drop) or a client
// timeout. A normal error response (401, 404, 500, etc.) is never retried.
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

const api = axios.create({ baseURL: BASE, timeout: REQUEST_TIMEOUT_MS });

// Offline copies of the reads a harvest weigh-in at the farm needs (the
// session/tenant, the farmer list and a farmer's detail), so the Farmers
// page still opens with no network. Every successful read of these is
// saved; when the network is gone, the saved copy is served instead.
// Scoped to one login -- cleared whenever the token changes.
const CACHE_PREFIX = "agri_cache:";
const OFFLINE_GET_PATHS = [
  /^\/auth\/verify$/,
  /^\/farmers$/,
  /^\/farmers\/[^/]+$/,
  /^\/arecanut\/dashboard$/,
];

function offlineCacheKey(config) {
  if ((config.method || "get").toLowerCase() !== "get") return null;
  const [path, qs] = (config.url || "").split("?");
  if (!OFFLINE_GET_PATHS.some((re) => re.test(path))) return null;
  const params = new URLSearchParams(qs || "");
  Object.entries(config.params || {}).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
  // A farmer's detail is fetched "as of" today; offline on a later day,
  // the last copy saved is still the best one available.
  params.delete("as_of");
  params.sort();
  const q = params.toString();
  return `${CACHE_PREFIX}${path}${q ? `?${q}` : ""}`;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, at: new Date().toISOString() }));
  } catch {
    // storage full or unavailable -- offline copy is best-effort
  }
}

function clearOfflineCache() {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX)).forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore unavailable storage
  }
}

const cachedResponse = (config, entry) => ({
  data: entry.data, status: 200, statusText: "OK", headers: {}, config, request: {},
  fromCache: true, cachedAt: entry.at,
});

// Whether a saved copy of this read exists on the device yet.
export const hasOfflineCopy = (url) => {
  const key = offlineCacheKey({ url });
  return !!(key && readCache(key));
};

export const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { clearOfflineCache(); localStorage.setItem(TOKEN_KEY, t); };
export const clearToken = () => { clearOfflineCache(); localStorage.removeItem(TOKEN_KEY); };

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Known offline: answer from the saved copy straight away instead of
  // waiting out the timeout + retries first.
  const key = offlineCacheKey(config);
  const entry = key && isOffline() ? readCache(key) : null;
  if (entry) config.adapter = () => Promise.resolve(cachedResponse(config, entry));
  return config;
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

api.interceptors.response.use(
  (r) => {
    const key = !r.fromCache && offlineCacheKey(r.config);
    if (key) writeCache(key, r.data);
    return r;
  },
  async (err) => {
    if (err.response && err.response.status === 401) {
      clearToken();
      window.dispatchEvent(new Event("agri-unauth"));
      return Promise.reject(err);
    }

    const config = err.config;
    const isTimeout = err.code === "ECONNABORTED";
    const isNetworkError = !err.response && err.code !== "ECONNABORTED";
    const isRetriable = config && (isTimeout || isNetworkError);

    if (isRetriable && !isOffline()) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= MAX_RETRIES) {
        await sleep(RETRY_DELAY_MS * config._retryCount);
        return api(config);
      }
    }
    // Network gone (or too flaky to answer): fall back to the saved copy.
    const key = config && isRetriable && offlineCacheKey(config);
    const entry = key && readCache(key);
    if (entry) return cachedResponse(config, entry);
    return Promise.reject(err);
  }
);
export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const blob = new Blob([res.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export const API_BASE = BASE;
export default api;

